import util from 'util'
import Promise from 'bluebird'
import moment from 'moment'
import { vals } from '../../util'
import api from '../../axios_api'
import _ from 'lodash'
import { UV_UDP_REUSEADDR } from 'constants'
import router from '../../main'

const stringify = JSON.stringify
const READ_INTERVAL = 30 // cycle counts (30 cycles per read ~30s)
const WRITE_INTERVAL = 5 // cycle counts (5 cycles per write ~5s)
/**
 * Attachments {
 * profile: user information (empty)
 * company: company information (filled)
 * uaid: unique attachment id (public)
 * url: url related to attachment (linkedin, etc)
 * name: name of attachment (unique in context/company context)
 * }
 */

const state = {
  server_data: [],
  local_add: [], // list of new attachment to add
  local_delete: [], // list of saved attachment to delete
  local_update: [], // list of saved attachment to modify
  has_data: false,
  next_id: 0,
  _write_timer: 0,
  _force_fetch: false,
  _read_timer: 0,
  _processing_cycle: false // necessary to make sure we aren't doing multiple reads/writes at the same time (single thread for now)
}

const getters = {
  attachmentData(state) {
    // take our serverdata, remove any in local_delete, take all in update and add all in add
    let retval = _.map(state.server_data, data => {
      if (_.indexOf(state.local_delete, data._id) >= 0) return null
      let index = _.findIndex(state.local_update, function (u) { return u._id === data._id })
      if (index >= 0) return state.local_update[index]
      return data
    })
    retval = _.concat(retval, state.local_add)
    return _.without(retval, null, undefined)
  },
  hasData(state) {
    return state.has_data || state.server_data.length > 0
  },
  isDirty(state) {
    return state.local_add.length > 0 || state.local_delete.length > 0 || state.local_update.length > 0
  },
  nextId(state) {
    return (state.next_id++)
  }
}

const mutations = {
  initialize(state) {
    // revert the portfolio back to '0'
    state.server_data = []
    state.local_add = [] // list of new attachment to add
    state.local_delete = [] // list of saved attachment to delete
    state.local_update = [] // list of saved attachment to modify
    state.has_data = false
    state.next_id = 0
    state._write_timer = 0
    state._force_fetch = false
    state._read_timer = 0
    state._processing_cycle = false
  },
  markData(state, payload) {
    state.has_data = payload.hasData || state.server_data.length > 0
  },
  overwrite(state, payload) {
    // stomp on our current data, this will wipe out local changes
    state.server_data = payload
    // stomp local changes
    state.local_add = []
    state.local_delete = []
    state.local_update = []
  },
  clean_add(state, payload) {
    state.local_add = _.filter(state.local_add, function (a) { return (_.findIndex(payload, function (i) { return i.company == a.company && i.context == a.context && i.name == a.name }) < 0) })
    state.local_add = _.without(state.local_add, null, undefined)
  },
  clean_delete(state, payload) {
    state.local_delete = _.without(state.local_delete, payload)
    state.local_delete = _.without(state.local_delete, null, undefined)
  },
  clean_update(state, payload) {
    state.local_update = _.filter(state.local_update, function (u) { return (_.findIndex(payload, function (i) { return i._id === u._id }) < 0) })
    state.local_update = _.without(state.local_update, null, undefined)
  },
  merge(state, payload) {
    // general merge with our server_data
    state.server_data = _.map(state.server_data, item => {
      let index = _.findIndex(payload, function (p) { return p._id === item._id })
      if (index >= 0) return _.merge(item, _.first(_.pullAt(payload, index)))
      return item
    })
    if (!_.isEmpty(payload)) state.server_data = _.concat(state.server_data, payload)
    state.server_data = _.without(state.server_data, undefined, null)
  },
  merge_add(state, payload) {
    // merge all added
    if (!_.isArray(payload)) payload = [payload]
    state.server_data = _.concat(state.server_data, payload)
    state.server_data = _.without(state.server_data, null, undefined)
  },
  merge_delete(state, payload) {
    // remove all that were deleted
    if (!_.isArray(payload)) payload = [payload]
    state.server_data = _.map(state.server_data, data => {
      return (_.indexOf(payload, data.uaid) >= 0) ? null : data
    })
    state.server_data = _.without(state.server_data, null, undefined)
  },
  merge_update(state, payload) {
    // take all updated
    if (!_.isArray(payload)) payload = [payload]
    state.server_data = _.map(state.server_data, data => {
      if (_.isNil(data)) return data
      let index = _.findIndex(payload, function (u) { return u._id === data._id })
      if (index >= 0) return _.first(_.pullAt(payload, index))
      return data
    })
    state.server_data = _.without(state.server_data, null, undefined)
  },
  update_add(state, payload) {
    payload = _.map(payload, item => {
      item._dirty = moment()
      return item
    })
    state.local_add = _.map(state.local_add, cache => {
      let index = _.findIndex(payload, function (p) { return p.company === cache.company && p.context === cache.context && p.name === cache.name })
      if (index >= 0) return _.first(_.pullAt(payload, index))
      return cache
    })
    if (!_.isEmpty(payload)) state.local_add = _.concat(state.local_add, payload)
  },
  update_delete(state, payload) {
    // delete is an array of uaids (no objects)
    if (!_.isArray(payload)) payload = [payload]
    state.local_delete = _.union(state.local_delete, payload)
  },
  update_update(state, payload) {
    if (!_.isArray(payload)) payload = [payload]
    payload = _.map(payload, item => {
      if (_.isNil(item)) return null
      item._dirty = (moment())
      return item
    })
    payload = _.without(payload, null, undefined)
    state.local_update = _.map(state.local_update, cache => {
      let index = _.findIndex(payload, function (p) { return p._id === cache._id })
      if (index >= 0) return _.first(_.pullAt(payload, index))
      return cache
    })
    if (!_.isEmpty(payload)) state.local_update = _.concat(state.local_update, payload)
  }
}

const actions = {
  start({ state, dispatch }) {
    state._write_timer = 0
    state._force_fetch = true
    state._processing_cycle = false
    // do a force overwrite fetch
    dispatch('apiCleanFetchAttachment')
      .then(() => { })
      .catch(() => { })
  },
  stop({ state, commit }) {
    // clean up
    commit('initialize')
    state._processing_cycle = false
  },
  cycle({ state, getters, dispatch, rootGetters }) {
    state._write_timer += 1
    state._read_timer += 1
    if (!state._processing_cycle && rootGetters['user/hasToken']) {
      state._processing_cycle = true
      // check to see if we need to do any writes
      if (getters['isDirty'] && state._write_timer >= WRITE_INTERVAL) {
        state._write_timer = 0
        // which write should we do?
        // do either, update, add, or delete
        let path = '_apiPatchAttachment'
        path = (!_.isNil(state.local_add) && !_.isEmpty(state.local_add)) ? `_apiPostAttachment` : path
        path = (!_.isNil(state.local_delete) && !_.isEmpty(state.local_delete)) ? `_apiDeleteAttachment` : path
        return dispatch(path)
          .then(fail => {
            if (!_.isNil(fail) && !_.isEmpty(fail)) dispatch('forceRead')
            state._processing_cycle = false
          })
          .catch(err => {
            state._processing_cycle = false
          })
      } else if (state._read_timer >= READ_INTERVAL) {
        state._read_timer = 0
        // get update from our server
        return dispatch('apiFetchAttachment')
          .then(() => {
            state._processing_cycle = false
          })
          .catch(err => {
            console.error(`Error reading: ${err}`)
            state._processing_cycle = false
          })
      } else {
        state._processing_cycle = false // not doing anything
      }
    }
  },
  clean({ commit }, payload) {
    // run each of our cleans
    commit('clean_add', payload.add || [])
    commit('clean_delete', payload.delete || [])
    commit('clean_update', payload.update || [])
  },
  merge({ commit }, payload) {
    // merge should have: delete, add, update
    if (!_.isNil(payload.add)) {
      commit('merge_add', payload.add)
    }
    if (!_.isNil(payload.delete)) {
      commit('merge_delete', payload.deleted)
    }
    if (!_.isNil(payload.update)) {
      commit('merge_udpate', payload.update)
    }
  },
  update({ commit }, payload) {
    // update our 3 caches
    if (!_.isEmpty(payload.add)) {
      commit('update_add', payload.add)
    }
    if (!_.isEmpty(payload.delete)) {
      commit('update_delete', payload.delete)
    }
    if (!_.isEmpty(payload.update)) {
      commit('update_update', payload.update)
    }
  },
  removeCompany({ dispatch }, payload) {
    // delete all attachments related to a company
    return dispatch('apiDeleteAttachmentsForCompany', payload)
  },
  forceRead({ state }) {
    state._read_timer = READ_INTERVAL
  },
  forceWrite({ state }) {
    state._write_timer = WRITE_INTERVAL
  },
  apiPostAttachment({ commit, dispatch }, payload) {
  },
  apiPatchAttachment({ state, commit }) {
  },
  apiFetchAttachment({ state, commit }) {
    return new Promise((resolve, reject) => {
      let params = {}
      if (state._force_fetch) {
        params.force = true
        state._force_fetch = false
      }
      return api.get('/attachment', { params })
        .then(res => {
          // merge the data with our local info
          commit('merge', res.data.encrypt.data)
          return resolve(res.data.encrypt.data)
        })
        .catch(err => {
          return reject(err)
        })
    })
  },
  apiCleanFetchAttachment({ commit }) {
    // full fetch from the server, overwriting local infos
    return new Promise((resolve, reject) => {
      let params = {}
      params.force = true
      return api.get('/attachment', { params })
        .then(res => {
          // merge the data with our local info
          commit('overwrite', res.data.encrypt.data)
          return resolve(res.data.encrypt.data)
        })
        .catch(err => {
          return reject(err)
        })
    })
  },
  apiDeleteAttachmentsForCompany({ dispatch, rootGetters }, payload) {
    // delete all attachments for company, then do a hard fetch
    if (_.isNil(payload) || _.isEmpty(payload) || !payload.ucid) return Promise.reject({ error: 'Missing requried paraeters.' })
    return api.delete(`/${router.currentRoute.params.account_id}/${router.currentRoute.params.workspace_id}/company/attachment/${payload.ucid}`)
      .then(res => {
        // clean, hard fetch
        return dispatch('apiCleanFetchAttachment')
      })
      .catch(err => {
        console.error(`Error deleting attachments: ${err}`)
        return Promise.reject({ error: err })
      })
  },
  _apiPatchAttachment({ commit, state }) {
    // do any updates based on our local cache
    return new Promise((resolve, reject) => {
      let payload = _.map(state.local_update, item => {
        // add a _sent item to the cache
        return _.omit(_.assign(item, { _sent: Date.now() }), ['_sent', '_dirty'])
      })
      return api.patch('/attachment', { encrypt: { data: payload } })
        .then(res => {
          let success = res.data.encrypt.success
          // merge/clean our successes
          commit('merge_update', _.clone(success))
          commit('clean_update', _.clone(success))
          // return our failed
          return resolve(res.data.encrypt.fail || [])
        })
        .catch(err => {
          console.error(`Error during patch: ${err}`)
          return reject(err)
        })
    })
  },
  _apiPostAttachment({ commit, state }) {
    // do a post op based on our local cache
    return new Promise((resolve, reject) => {
      let payload = _.map(state.local_add, item => {
        // add a _sent item to the cache
        return _.omit(_.assign(item, { _sent: Date.now() }), ['_sent', '_dirty'])
      })
      return api.post('/attachment', { encrypt: { data: payload } })
        .then(res => {
          let success = res.data.encrypt.success
          // merge/clean our success
          commit('merge_add', _.clone(success))
          commit('clean_add', _.clone(success))
          // return our failed
          return resolve(res.data.encrypt.fail || [])
        })
        .catch(err => {
          console.error(`Error during post: ${err}`)
          return reject(err)
        })
    })
  },
  _apiDeleteAttachment({ commit, state, rootGetters }) {
    // do a delete op based on our local cache
    return new Promise((resolve, reject) => {
      // have to delete users one at a time
      let currentRole = rootGetters['role/current_role'];
      let url = `/attachment/${uaid}`
      // if (currentRole && currentRole._id) {
      //   url = `/attachment/${uaid}?account=${currentRole.account}`;
      // } else url =`/attachment/${uaid}`
      return Promise.map(state.local_delete, uaid => {
        return api.delete(url)
          .then(() => {
            // merge/clean our success
            commit('merge_delete', _.clone(uaid))
            commit('clean_delete', _.clone(uaid))
            return Promise.resolve()
          })
          .catch(err => {
            console.error(`Error during delete: ${err}`)
            commit('merge_delete', _.clone(uaid))
            commit('clean_delete', _.clone(uaid))
            return Promise.resolve()
          })
      })
        .then(() => {
          return resolve({})
        })
        .catch(err => {
          console.error(`Error during deletes: ${err}`)
          return reject(err)
        })
    })
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}